import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AgentFace } from '@common-components/mascot/AgentFace';
import { toneForAgent } from '@common-components/mascot/tones';
import { namespace } from '@common-config/constants';
import {
  backendApi,
  MyBridgeAgent,
  MyBridgeChatSessionRow,
  MyBridgeConversationTakeoverPatch,
  MyBridgeTranscriptTurn,
} from '@common/reducers/backend-api/backend-api';
import { Brain, ChevronDown, Loader2, MessageCircle, MessageSquare, MessagesSquare, MoreHorizontal, Pencil, RotateCcw, Send, Terminal, Trash2, Wrench } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirmDialog } from '@common/components/use-confirm-dialog';
import { useBridgeChatNames } from './useBridgeChatNames';
import { takeoverSinceKey } from './takeover-source';
import { formatResumeTime, pausedBadgeFor } from './paused-badge';
import { PausedChatsBadge } from './PausedChats';
import { toast } from 'sonner';

/**
 * Shared my-bridge UI — the style tokens, relative-time formatting, and the agent
 * inspector pieces (per-chat sessions, the conversation, the debug timeline, the
 * Claude-session transcript, and the management actions: take over / release a
 * chat, delete one chat's Claude session, reset all sessions). Used by BOTH the
 * admin `/bridges` inspector and the owner's `/agents` page, so there is one
 * implementation of "see and manage a Claude agent's conversations".
 */

export const muted = { color: 'var(--color-text-muted)' } as const;
export const text = { color: 'var(--color-text)' } as const;

/** The one-line purpose of an agent: the first non-empty line of its instructions. */
export const agentPurpose = (agent: Pick<MyBridgeAgent, 'identityPrompt'>): string =>
  (agent.identityPrompt ?? '')
    .split('\n')
    .map((line) => line.replace(/^[#>*\-\s]+/, '').trim())
    .find(Boolean) ?? '';
const ACTIVE_GREEN = '#16a34a';
/** A live Claude session — the green an operator scans the list for. */
const SESSION_LIVE_GREEN = '#16a34a';
const TAKEOVER_AMBER = '#d97706';
const chipStyle: React.CSSProperties = { background: 'var(--color-surface-muted, rgba(0,0,0,0.06))', color: 'var(--color-text)' };

/** A localized "5m ago" formatter (ISO string or epoch ms); '—' when unusable. */
export function useTimeAgo(): (value?: string | number | null) => string {
  const { i18n } = useTranslation(namespace);
  return useMemo(() => {
    const rtf = new Intl.RelativeTimeFormat(i18n.language, { style: 'narrow' });
    return (value) => {
      const ms = typeof value === 'number' ? value : value ? new Date(value).getTime() : NaN;
      if (!Number.isFinite(ms) || ms <= 0) return '—';
      const s = Math.max(0, Math.floor((Date.now() - ms) / 1000));
      if (s < 60) return rtf.format(-s, 'second');
      if (s < 3600) return rtf.format(-Math.floor(s / 60), 'minute');
      if (s < 86400) return rtf.format(-Math.floor(s / 3600), 'hour');
      return rtf.format(-Math.floor(s / 86400), 'day');
    };
  }, [i18n.language]);
}

/** A chat message's timestamp (WhatsApp `t` is seconds; some rows carry ms) → a
 *  locale string, or '' when it isn't a usable number (avoids "Invalid Date"). */
function formatMsgTime(ts?: number): string {
  if (typeof ts !== 'number' || !Number.isFinite(ts) || ts <= 0) return '';
  return new Date(ts < 1e12 ? ts * 1000 : ts).toLocaleString();
}

/** A bridge's status badge — green for a live bridge ('ready' or 'connected'). */
export const BridgeStatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <Badge variant={status === 'ready' || status === 'connected' ? 'default' : 'secondary'}>{status}</Badge>
);

/** The pod/host that runs a session — proves cloud execution + shows overflow-pod distribution. */
const PodChip: React.FC<{ pod: string; maxWidth: number; testId: string }> = ({ pod, maxWidth, testId }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span
        className="text-[10px] font-mono flex-none px-1.5 py-0.5 rounded truncate"
        style={{ ...chipStyle, maxWidth }}
        data-testid={testId}
      >
        ⬡ {pod}
      </span>
    </TooltipTrigger>
    <TooltipContent>{pod}</TooltipContent>
  </Tooltip>
);

const Spinner: React.FC<{ size?: string; pad?: string }> = ({ size = 'h-4 w-4', pad = 'py-4' }) => (
  <div className={`${pad} flex justify-center`}><Loader2 className={`${size} animate-spin`} style={muted} /></div>
);

/** One per-chat session row — is a session live, is a human holding it, and the per-chat actions. */
const ChatSessionRow: React.FC<{
  agentId: string;
  row: MyBridgeChatSessionRow;
  bridgeId?: string;
  canManage?: boolean;
  expanded: boolean;
  onToggle: () => void;
}> = ({ agentId, row, bridgeId, canManage, expanded, onToggle }) => {
  const { t } = useTranslation(namespace);
  const { confirm, dialog } = useConfirmDialog();
  const ago = useTimeAgo();
  const { nameOf } = useBridgeChatNames();
  const open = row.session?.open === true;
  const held = row.takeover?.held === true;
  // A session with no conversation (a schedule run) has no takeover / fresh-conversation actions.
  const hasConversation = row.takeover !== null && !!row.chatId;
  const outcome = row.schedule?.outcome;
  const statusLabel = held
    ? t('bridgeInspector.statusTakeover')
    : open
      ? t('bridgeInspector.statusActive')
      : row.session?.attempted
        ? t('bridgeInspector.statusClosed')
        : t('bridgeInspector.statusNone');

  // THE takeover action (the same one the conversations panel uses).
  const [setTakeover, { isLoading: takingOver }] = backendApi.useSetMyBridgeConversationTakeoverMutation();
  const [fresh, { isLoading: freshing }] = backendApi.useFreshMyBridgeAgentConversationMutation();

  // DELETE THE CLAUDE SESSION of this conversation ("fresh conversation", v2:
  // `agent_conversations.resetAt`): earlier history stops being context and the
  // chat's next session starts a brand-new Claude session WITH THE CURRENT PROMPT
  // instead of resuming. A running turn finishes first; nothing else is touched.
  const deleteClaudeSession = async () => {
    if (!row.chatId) return;
    if (!(await confirm({ content: t('bridgeInspector.deleteSessionConfirm'), title: t('confirmDialog.title'), confirmLabel: t('confirmDialog.startFresh'), destructive: false }))) return;
    try { await fresh({ agentId, chatId: row.chatId, bridgeId }).unwrap(); } catch { /* the row refetches either way */ }
  };
  const toggleTakeover = async (next: boolean) => {
    if (!row.chatId) return;
    try {
      await setTakeover({ agentId, chatId: row.chatId, status: next, bridgeId }).unwrap();
      if (!next) toast.success(t('myBridge.paused.hint')); // FE#484: what arrived while paused stays unanswered
    } catch { /* row stays authoritative */ }
  };

  return (
    <div data-testid="inspector-chat-row">
      {dialog}
      <div className="flex items-center gap-3 py-2 px-1">
        {/* A real button, not a clickable <div>: the row expands by keyboard too,
            and the actions on the right stay separately focusable. */}
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          className="flex-1 min-w-0 flex items-center gap-3 text-start hover:opacity-80"
        >
          <MessageSquare className="h-4 w-4 flex-none" style={muted} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate" style={text} dir="auto">{row.chatId ? nameOf(row.chatId) : (row.schedule?.occurrenceId ?? row.id)}</div>
            <div className="text-xs truncate" style={muted}>
              {/* The row's own id only: every conversation is its own session (no shared mode, F3). */}
              {row.sessionId ? t('bridgeInspector.sessionShort', { id: row.sessionId.slice(0, 8) }) : t('bridgeInspector.noSessionId')}
              {row.session?.closedAt ? ` · ${t('bridgeInspector.closedAgo', { ago: ago(row.session.closedAt) })}` : ''}
              {row.updatedAt ? ` · ${t('bridgeInspector.updatedAgo', { ago: ago(row.updatedAt) })}` : ''}
            </div>
            {(outcome?.status === 'succeeded' || outcome?.status === 'failed') && (
              <div className="text-xs truncate" style={muted} data-testid="inspector-schedule-outcome">
                {outcome.status === 'succeeded'
                  ? t('bridgeInspector.scheduleSucceeded', { occurrence: row.schedule!.occurrenceId })
                  : t('bridgeInspector.scheduleFailed', { occurrence: row.schedule!.occurrenceId, reason: outcome.reason ?? '' })}
              </div>
            )}
            {row.failure && (
              <div className="text-xs truncate text-red-500" data-testid="inspector-session-failure">
                {/* The latest give-up or recorded operator notice — with no operator chat, the only place the owner sees it. */}
                {row.failure.name === 'operator_notice'
                  ? t('bridgeInspector.noticeAgo', { kind: row.failure.kind ?? '', ago: ago(row.failure.at) })
                  : row.failure.name === 'resume_failed'
                    ? t('bridgeInspector.resumeFailedAgo', { ago: ago(row.failure.at) })
                    : t('bridgeInspector.deliveryFailedAgo', { ago: ago(row.failure.at) })}
              </div>
            )}
          </div>
          {/* LEGACY field: only flag-off bridge rows carry `outcomes`; v2 rows never do. */}
          {(row.outcomes?.total ?? 0) > 0 && (
            <span className="text-xs flex-none" style={muted}>{t('bridgeInspector.turns', { count: row.outcomes!.total })}</span>
          )}
          {row.pod && <PodChip pod={row.pod} maxWidth={150} testId="inspector-session-pod" />}
          {/* A LIVE session reads green — the one state an operator scans for.
              Held is amber like the takeover switch (a human has the chat), closed/none stay muted.
              Explicit colours: the theme's `destructive` token is unset here, which left the badge invisible. */}
          <Badge
            variant={held || open ? 'default' : 'secondary'}
            style={
              held
                ? { backgroundColor: TAKEOVER_AMBER, color: '#fff', borderColor: TAKEOVER_AMBER }
                : open
                  ? { backgroundColor: SESSION_LIVE_GREEN, color: '#fff', borderColor: SESSION_LIVE_GREEN }
                  : undefined
            }
          >
            {statusLabel}
          </Badge>
        </button>
        {hasConversation && (
          <>
            <Button
              variant="ghost"
              size="sm"
              className="flex-none"
              disabled={freshing}
              onClick={() => void deleteClaudeSession()}
              title={t('bridgeInspector.deleteSessionHint')}
              data-testid="chat-delete-session"
              style={{ color: '#dc2626' }}
            >
              {freshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              <span className="ms-1.5 hidden sm:inline">{t('bridgeInspector.deleteSession')}</span>
            </Button>
            {/* Per-chat TAKEOVER toggle — amber when a human holds the chat. */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1.5 flex-none">
                  <span className="text-[11px]" style={held ? { color: TAKEOVER_AMBER } : muted}>{held ? t('bridgeInspector.takeoverOn') : t('bridgeInspector.takeover')}</span>
                  <Switch
                    checked={held}
                    disabled={takingOver}
                    onCheckedChange={(v) => void toggleTakeover(v)}
                    aria-label={t('bridgeInspector.takeoverAria')}
                    style={held ? { backgroundColor: TAKEOVER_AMBER } : undefined}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>{held ? t('bridgeInspector.takeoverHeldHint') : t('bridgeInspector.takeoverHint')}</TooltipContent>
            </Tooltip>
          </>
        )}
      </div>
      {expanded && (
        <div className="ps-2 pb-2">
          {row.chatId && <ConversationViewer agentId={agentId} chatId={row.chatId} bridgeId={bridgeId} canRequeue={canManage && hasConversation} />}
          {row.chatId && <ActivityViewer agentId={agentId} chatId={row.chatId} bridgeId={bridgeId} />}
          {canManage && hasConversation && row.chatId && <TranscriptViewer agentId={agentId} chatId={row.chatId} bridgeId={bridgeId} />}
        </div>
      )}
    </div>
  );
};

/**
 * The conversation (message history) of one chat. `canRequeue` (operator) adds a hover
 * action on each customer bubble — "Re-queue from here": the customer messages held for
 * credits / budget from that one on are delivered to the agent now (0 when none are held).
 */
export const ConversationViewer: React.FC<{ agentId: string; chatId: string; bridgeId?: string; canRequeue?: boolean }> = ({ agentId, chatId, bridgeId, canRequeue }) => {
  const { t } = useTranslation(namespace);
  const { confirm, dialog } = useConfirmDialog();
  const { data, isLoading, isError, refetch } = backendApi.useGetMyBridgeAgentConversationQuery({ agentId, chatId, bridgeId, limit: 40 });
  const [requeue] = backendApi.useRequeueMyBridgeAgentFromHereMutation();
  const [requeueing, setRequeueing] = useState<string | null>(null);
  const requeueFrom = async (entryId: string) => {
    if (!(await confirm({ content: t('bridgeInspector.requeueConfirm'), title: t('confirmDialog.title'), confirmLabel: t('bridgeInspector.requeue'), destructive: false }))) return;
    setRequeueing(entryId);
    try {
      const { requeued } = await requeue({ agentId, chatId, entryId, bridgeId }).unwrap();
      if (requeued > 0) toast.success(t('bridgeInspector.requeueDone', { count: requeued }));
      else toast.info(t('bridgeInspector.requeueNone'));
      void refetch();
    } catch (err) {
      // 409 = a human holds the chat; 402 = still out of credits; anything else is generic.
      const status = (err as { status?: unknown })?.status;
      toast.error(status === 409 ? t('bridgeInspector.requeueTakeover') : status === 402 ? t('bridgeInspector.requeueNoCredits') : t('bridgeInspector.requeueError'));
    } finally {
      setRequeueing(null);
    }
  };
  if (isLoading) return <Spinner size="h-5 w-5" pad="py-6" />;
  if (isError) return <p className="text-sm py-3" style={muted}>{t('bridgeInspector.conversationError')}</p>;
  const messages = data?.messages ?? [];
  if (messages.length === 0) return <p className="text-sm py-3" style={muted}>{t('bridgeInspector.noMessages')}</p>;
  return (
    <div className="flex flex-col gap-1.5 py-2 max-h-96 overflow-y-auto">
      {dialog}
      {messages.map((m, i) => (
        <div key={m.id ?? i} className={`group flex items-center gap-1.5 ${m.fromMe ? 'justify-end' : 'justify-start'}`}>
          <div
            className="max-w-[80%] rounded-lg px-3 py-1.5 text-sm"
            style={{
              background: m.fromMe ? 'var(--cta-button-bg, var(--color-primary))' : 'var(--color-surface-2, #f0f0f0)',
              color: m.fromMe ? 'var(--cta-button-text, #fff)' : 'var(--color-text)',
            }}
          >
            <div className="whitespace-pre-wrap break-words" dir="auto">{m.text || <em style={muted}>[{m.type || t('bridgeInspector.nonText')}]</em>}</div>
            <div className="text-[10px] mt-0.5 opacity-70">{formatMsgTime(m.timestamp)}</div>
          </div>
          {canRequeue && !m.fromMe && m.id && (
            <Button
              variant="ghost"
              size="sm"
              className={`flex-none h-7 px-2 text-xs transition-opacity ${requeueing === m.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100'}`}
              disabled={requeueing !== null}
              onClick={() => { if (m.id) void requeueFrom(m.id); }}
              title={t('bridgeInspector.requeueHint')}
              data-testid="conversation-requeue"
            >
              {requeueing === m.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              <span className="ms-1.5">{t('bridgeInspector.requeue')}</span>
            </Button>
          )}
        </div>
      ))}
    </div>
  );
};

/** Level → colour, so the debug timeline is scannable at a glance. */
const LEVEL_STYLE: Record<string, { bg: string; fg: string }> = {
  reply: { bg: 'rgba(34,197,94,0.15)', fg: ACTIVE_GREEN },
  status: { bg: 'rgba(245,158,11,0.15)', fg: TAKEOVER_AMBER },
  debug: { bg: 'rgba(107,114,128,0.15)', fg: '#6b7280' },
  presence: { bg: 'rgba(59,130,246,0.15)', fg: '#2563eb' },
};
const LEVEL_FILTERS = [
  { key: 'reply', labelKey: 'bridgeInspector.levelReplyOnly' },
  { key: 'status', labelKey: 'bridgeInspector.levelStatus' },
  { key: 'debug', labelKey: 'bridgeInspector.levelDebug' },
];

/** The DEBUG TIMELINE of one chat — a projection of the Bridge audit feed. */
const ActivityViewer: React.FC<{ agentId: string; chatId: string; bridgeId?: string }> = ({ agentId, chatId, bridgeId }) => {
  const { t } = useTranslation(namespace);
  const [level, setLevel] = useState('reply');
  const { data, isLoading, isError, isFetching, refetch } = backendApi.useGetMyBridgeAgentActivityQuery({ agentId, chatId, bridgeId, level, limit: 300 });
  const events = data?.events ?? [];
  return (
    <div className="py-2 border-t mt-1" style={{ borderColor: 'var(--color-border)' }}>
      <div className="flex items-center gap-2 mb-2">
        <Terminal className="h-3.5 w-3.5" style={muted} />
        <span className="text-xs font-semibold" style={muted}>{t('bridgeInspector.activity')}</span>
        <div className="flex gap-1 ms-auto" role="group" aria-label={t('bridgeInspector.filterByLevel')}>
          {LEVEL_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setLevel(f.key)}
              aria-pressed={level === f.key}
              data-testid={`activity-level-${f.key}`}
              className="text-[11px] px-2 py-0.5 rounded-md border transition-colors"
              style={
                level === f.key
                  ? { background: LEVEL_STYLE[f.key].bg, color: LEVEL_STYLE[f.key].fg, borderColor: LEVEL_STYLE[f.key].fg }
                  : { ...muted, borderColor: 'var(--color-border)' }
              }
            >
              {t(f.labelKey)}
            </button>
          ))}
        </div>
      </div>
      {isLoading ? (
        <Spinner />
      ) : isError && events.length === 0 ? (
        // Only a HARD failure (no cached rows) shows the error; a transient blip
        // during a level switch keeps the last rows on screen instead of flashing.
        <p className="text-xs py-2" style={muted}>{t('bridgeInspector.activityError')} <button className="underline" onClick={() => refetch()}>{t('bridgeInspector.retry')}</button></p>
      ) : events.length === 0 ? (
        <p className="text-xs py-2" style={muted}>{t('bridgeInspector.noActivity')}</p>
      ) : (
        <div className="flex flex-col gap-0.5 max-h-80 overflow-y-auto" style={{ opacity: isFetching ? 0.6 : 1 }}>
          {events.map((row, i) => {
            const ls = LEVEL_STYLE[row.level] ?? LEVEL_STYLE.debug;
            return (
              <div key={i} className="flex items-start gap-2 text-xs py-1 px-1 rounded hover:bg-black/5">
                <span className="text-[10px] font-mono flex-none mt-0.5" style={muted}>{row.at ? new Date(row.at).toLocaleTimeString() : '—'}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded flex-none font-medium" style={{ background: ls.bg, color: ls.fg }}>{row.level}</span>
                {row.tool ? <Wrench className="h-3 w-3 flex-none mt-0.5" style={{ color: ls.fg }} /> : null}
                <span className="flex-1 break-words" style={text}>
                  {row.note}
                  {row.ms != null ? <span style={muted}> · {t('myBridge.inspect.durationMs', '{{ms}}ms', { ms: row.ms })}</span> : null}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** Style per transcript role — thinking is the reasoning scratchpad; tools are tool calls/results. */
const ROLE_STYLE: Record<MyBridgeTranscriptTurn['role'], { bg: string; fg: string }> = {
  thinking: { bg: 'rgba(139,92,246,0.12)', fg: '#7c3aed' },
  tool: { bg: 'rgba(107,114,128,0.12)', fg: '#4b5563' },
  assistant: { bg: 'rgba(34,197,94,0.10)', fg: ACTIVE_GREEN },
  user: { bg: 'rgba(59,130,246,0.10)', fg: '#2563eb' },
  system: { bg: 'rgba(245,158,11,0.10)', fg: TAKEOVER_AMBER },
};

/**
 * A Claude session's turns (messages, tool calls, thinking), colour-coded by role.
 */
const TranscriptTurns: React.FC<{ turns: MyBridgeTranscriptTurn[] }> = ({ turns }) => {
  const { t } = useTranslation(namespace);
  const ago = useTimeAgo();
  return (
    <div className="flex flex-col gap-1 mt-2 max-h-96 overflow-y-auto">
      {turns.map((tn, i) => {
        const rs = ROLE_STYLE[tn.role] ?? ROLE_STYLE.system;
        const label = t(`bridgeInspector.roles.${tn.role}`);
        return (
          <div key={i} className="text-xs rounded-md px-2 py-1.5" style={{ background: rs.bg }}>
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="font-medium" style={{ color: rs.fg }}>{tn.toolName ? `${label}: ${tn.toolName}` : label}</span>
              {tn.at ? <span className="text-[10px]" style={muted}>{ago(tn.at)}</span> : null}
            </div>
            <div className="whitespace-pre-wrap break-words" dir="auto" style={tn.role === 'thinking' ? { color: rs.fg, fontStyle: 'italic' } : text}>
              {tn.text || <em style={muted}>{t('bridgeInspector.noText')}</em>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * One chat session's Claude transcript — the real turns behind the replies:
 * the model's THINKING (reasoning), the tool calls + results, and the messages.
 * This is the "why did it reply that / what did it do" view the customer
 * conversation hides. Operator-gated (the caller passes it only when canManage),
 * and collapsed by default because it is long.
 */
const TranscriptViewer: React.FC<{ agentId: string; chatId: string; bridgeId?: string }> = ({ agentId, chatId, bridgeId }) => {
  const { t } = useTranslation(namespace);
  const [show, setShow] = useState(false);
  const { data, isLoading, isError, refetch } = backendApi.useGetMyBridgeAgentTranscriptQuery(
    { agentId, chatId, bridgeId, limit: 60, includeThinking: true },
    { skip: !show },
  );
  const turns = data?.turns ?? [];
  return (
    <div className="py-2 border-t mt-1" style={{ borderColor: 'var(--color-border)' }}>
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        aria-expanded={show}
        className="flex items-center gap-2 text-xs font-semibold"
        style={muted}
        data-testid="transcript-toggle"
      >
        <Brain className="h-3.5 w-3.5" style={{ color: ROLE_STYLE.thinking.fg }} />
        {t('bridgeInspector.transcriptToggle')}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${show ? 'rotate-180' : ''}`} />
      </button>
      {show && (
        isLoading ? (
          <Spinner />
        ) : isError ? (
          <p className="text-xs py-2" style={muted}>{t('bridgeInspector.transcriptError')} <button className="underline" onClick={() => refetch()}>{t('bridgeInspector.retry')}</button></p>
        ) : turns.length === 0 ? (
          <p className="text-xs py-2" style={muted}>{t('bridgeInspector.noTranscript')}</p>
        ) : (
          <TranscriptTurns turns={turns} />
        )
      )}
    </div>
  );
};

const CONVERSATIONS_PAGE = 20;
const CONVERSATIONS_MAX = 200;

/**
 * rev 3.3 §6.7 — the agent's conversations (`agent_conversations`) with their
 * takeover state: the live takeover switch (manual, regardless of the flags) and
 * the conversation's OWN two flags (inherited from the agent at creation, then
 * independent). Owner surface only — the backend route is scoped to the caller.
 */
const TakeoverConversations: React.FC<{ agent: MyBridgeAgent }> = ({ agent }) => {
  const { t, i18n } = useTranslation(namespace);
  const ago = useTimeAgo();
  const { nameOf } = useBridgeChatNames();
  const [limit, setLimit] = useState(CONVERSATIONS_PAGE);
  const { data, isLoading, isError, isFetching } = backendApi.useGetMyBridgeAgentTakeoverConversationsQuery({ agentId: agent.id, limit });
  const [patch] = backendApi.useSetMyBridgeConversationTakeoverMutation();
  const [savingChat, setSavingChat] = useState<string | null>(null);
  /** null = ok; 'unavailable' = the takeover service is not running (503); 'failed' = anything else. */
  const [saveError, setSaveError] = useState<'unavailable' | 'failed' | null>(null);
  const rows = data?.conversations ?? [];

  const save = async (chatId: string, body: MyBridgeConversationTakeoverPatch) => {
    setSaveError(null);
    setSavingChat(chatId);
    try {
      await patch({ agentId: agent.id, chatId, ...body }).unwrap();
      // FE#484: releasing a chat does not answer what arrived while it was paused.
      if (body.status === false) toast.success(t('myBridge.paused.hint'));
    } catch (err) {
      // 503 = the takeover service (agents v2 runtime) is not running yet — say so
      // specifically; anything else is the generic retry message. The row stays
      // authoritative either way (it refetches).
      const status = (err as { status?: unknown })?.status;
      setSaveError(status === 503 ? 'unavailable' : 'failed');
    } finally {
      setSavingChat(null);
    }
  };

  return (
    <div className="mt-4" data-testid="takeover-conversations">
      <div className="text-xs font-semibold mb-1" style={muted}>{t('myBridge.conversations.title')}</div>
      {saveError && <p className="text-xs text-red-500 py-1" data-testid="takeover-conversations-error">{saveError === 'unavailable' ? t('myBridge.conversations.unavailable') : t('myBridge.conversations.saveError')}</p>}
      {isLoading ? (
        <Spinner size="h-5 w-5" />
      ) : isError ? (
        <p className="text-sm py-2" style={muted}>{t('myBridge.conversations.error')}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm py-2" style={muted}>{t('myBridge.conversations.empty')}</p>
      ) : (
        <div className="divide-y" style={{ borderColor: 'var(--color-border)', opacity: isFetching ? 0.7 : 1 }}>
          {rows.map((row) => {
            const tk = row.takeover;
            const busy = savingChat === row.chatId;
            const paused = pausedBadgeFor(tk);
            return (
              <div key={row.conversationId} className="flex flex-wrap items-center gap-x-4 gap-y-2 py-2 px-1" data-testid="takeover-conversation-row">
                <div className="flex-1 min-w-[180px]">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-medium truncate" style={text} dir="auto">{nameOf(row.chatId)}</span>
                    {paused && (
                      <Badge variant="secondary" className="flex-none" style={{ backgroundColor: 'rgba(245,158,11,0.15)', color: TAKEOVER_AMBER }} data-testid="conversation-paused-badge">
                        {t('myBridge.conversations.paused')}
                      </Badge>
                    )}
                  </div>
                  {paused ? (
                    <div className="text-xs" style={{ color: TAKEOVER_AMBER }} data-testid="conversation-paused-detail">
                      <div>{t(paused.reasonKey)} · {t('myBridge.conversations.pausedSince', { ago: ago(paused.since) })}</div>
                      <div>{paused.until ? t('myBridge.conversations.resumesAt', { time: formatResumeTime(paused.until, i18n.language) }) : t('myBridge.conversations.pausedUntilResume')}</div>
                    </div>
                  ) : (
                    <div className="text-xs truncate" style={tk.status ? { color: TAKEOVER_AMBER } : muted}>
                      {tk.status
                        ? t(takeoverSinceKey(tk.source), { ago: ago(tk.since) })
                        : row.lastActivityAt ? t('bridgeInspector.updatedAgo', { ago: ago(row.lastActivityAt) }) : ''}
                    </div>
                  )}
                </div>
                {/* The conversation's own flags (independent of the agent defaults). */}
                <label className="flex items-center gap-1.5 text-xs cursor-pointer" style={muted}>
                  <Switch
                    checked={tk.enabled}
                    disabled={busy}
                    onCheckedChange={(v) => void save(row.chatId, { enabled: v === true })}
                    style={tk.enabled ? { backgroundColor: 'var(--cta-button-bg)' } : undefined}
                    data-testid="conversation-takeover-enabled"
                  />
                  {t('myBridge.conversations.allowTakeover')}
                </label>
                <label className={`flex items-center gap-1.5 text-xs ${tk.enabled ? 'cursor-pointer' : 'opacity-60'}`} style={muted}>
                  <Switch
                    checked={tk.byIncomingMessage}
                    disabled={busy || !tk.enabled}
                    onCheckedChange={(v) => void save(row.chatId, { byIncomingMessage: v === true })}
                    style={tk.byIncomingMessage ? { backgroundColor: 'var(--cta-button-bg)' } : undefined}
                    data-testid="conversation-takeover-by-incoming"
                  />
                  {t('myBridge.conversations.autoTakeover')}
                </label>
                {paused && (
                  <Button variant="outline" size="sm" disabled={busy} onClick={() => void save(row.chatId, { status: false })} data-testid="conversation-resume">
                    {t('myBridge.conversations.resume')}
                  </Button>
                )}
                {/* The LIVE takeover — amber while a human holds the conversation. */}
                <div className="flex items-center gap-1.5 flex-none">
                  <span className="text-[11px]" style={tk.status ? { color: TAKEOVER_AMBER } : muted}>
                    {tk.status ? t('bridgeInspector.takeoverOn') : t('bridgeInspector.takeover')}
                  </span>
                  <Switch
                    checked={tk.status}
                    disabled={busy}
                    onCheckedChange={(v) => void save(row.chatId, { status: v === true })}
                    aria-label={t('bridgeInspector.takeoverAria')}
                    style={tk.status ? { backgroundColor: TAKEOVER_AMBER } : undefined}
                    data-testid="conversation-takeover-status"
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
      {data?.hasMore && limit < CONVERSATIONS_MAX && (
        <Button variant="ghost" size="sm" className="mt-1" onClick={() => setLimit((l) => Math.min(l + CONVERSATIONS_PAGE, CONVERSATIONS_MAX))}>
          {t('myBridge.conversations.loadMore')}
        </Button>
      )}
    </div>
  );
};

/**
 * Full detail + management for one agent: config, per-chat sessions
 * (each with the conversation, activity, takeover toggle, delete-session), and
 * "reset all sessions". `canManage` gates the operator-only views/actions (the
 * transcript, reset all).
 */
export const AgentInspectPanel: React.FC<{
  agent: MyBridgeAgent;
  bridgeId?: string;
  canManage?: boolean;
  /** The viewer owns this agent (the `/agents` page) -> show the rev 3.3 takeover conversations. */
  isOwner?: boolean;
}> = ({ agent, bridgeId, canManage, isOwner }) => {
  const { t } = useTranslation(namespace);
  const { data: cs, isLoading, isError } = backendApi.useGetMyBridgeAgentChatSessionsQuery({ agentId: agent.id, bridgeId });
  const [openChatId, setOpenChatId] = useState<string | null>(null);
  const [resetAll, { isLoading: resetting }] = backendApi.useResetMyBridgeAgentSessionsMutation();
  const sessions = cs?.sessions ?? [];
  // Rows come newest activity first (server: conversation lastActivityAt, else the session's updatedAt),
  // so this is the pod of the most recently active row that has one (each row also shows its own).
  const pod = sessions.find((s) => s.pod)?.pod ?? null;

  return (
    <Card data-testid="agent-inspect-panel">
      <CardHeader>
        <div className="flex items-center gap-2">
          <AgentFace agent={agent} size={24} level="static" />
          <CardTitle className="text-base" dir="auto" style={{ unicodeBidi: 'plaintext' }}>
            {agent.label}
          </CardTitle>
          <Badge variant={agent.enabled ? 'default' : 'secondary'}>{agent.enabled ? t('bridgeInspector.enabled') : t('bridgeInspector.paused')}</Badge>
          {pod && <PodChip pod={pod} maxWidth={180} testId="agent-pod" />}
          {canManage && sessions.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="ms-auto"
              disabled={resetting}
              onClick={() => void resetAll({ agentId: agent.id, bridgeId })}
              title={t('bridgeInspector.resetAllHint')}
            >
              {resetting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
              <span className="ms-1.5">{t('bridgeInspector.resetAll')}</span>
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mb-3">
          <div><div style={muted}>{t('bridgeInspector.model')}</div><div style={text}>{agent.model || t('bridgeInspector.modelDefault')}</div></div>
          <div><div style={muted}>{t('bridgeInspector.chats')}</div><div style={text}>{agent.chatIds?.length ?? 0}</div></div>
        </div>

        <div className="text-xs font-semibold mb-1" style={muted}>{t('bridgeInspector.sessionsTitle')}</div>
        {isLoading ? (
          <Spinner size="h-5 w-5" />
        ) : isError ? (
          <p className="text-sm py-2" style={muted}>{t('bridgeInspector.sessionsError')}</p>
        ) : sessions.length === 0 ? (
          <p className="text-sm py-2" style={muted}>{t('bridgeInspector.noSessions')}</p>
        ) : (
          <div className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
            {sessions.map((row) => {
              const key = row.id ?? row.chatId ?? '';
              return (
                <ChatSessionRow
                  key={key}
                  agentId={agent.id}
                  row={row}
                  bridgeId={bridgeId}
                  canManage={canManage}
                  expanded={openChatId === key}
                  onToggle={() => setOpenChatId(openChatId === key ? null : key)}
                />
              );
            })}
          </div>
        )}
        {isOwner && <TakeoverConversations agent={agent} />}
      </CardContent>
    </Card>
  );
};

/**
 * SHARED agent row — one component, one logic for BOTH the owner's `/agents`
 * page and the admin `/bridges` inspector.
 *
 * One line per agent: its face (asleep when paused) · name + one-line purpose ·
 * ONE status control · a `⋯` menu (edit prompt, conversations, delete).
 * With `onOpen` the whole row opens the agent (a plain click; Ctrl/⌘/middle-click
 * on the name follows `openHref` in a new tab). Management renders only when its
 * handler is passed, so the inspector uses it read-only (status as a badge).
 * "Conversations" expands the shared `AgentInspectPanel` under the row.
 */
export const AgentRow: React.FC<{
  agent: MyBridgeAgent;
  bridgeId?: string;
  canManage?: boolean;
  expanded?: boolean;
  /** Shows/hides the conversations panel under the row. */
  onToggleExpand?: () => void;
  /** Opens the agent's conversations on their own page (instead of expanding). */
  onOpenConversations?: (agent: MyBridgeAgent) => void;
  /** Hover reaches the host card's edges (the host pads its list px-3 sm:px-5). */
  fullBleed?: boolean;
  onToggleEnabled?: (agent: MyBridgeAgent) => void;
  toggling?: boolean;
  /** Opens the agent (the row click). */
  onOpen?: (agent: MyBridgeAgent) => void;
  /** The agent's own URL (a real link on the name; "Edit prompt" in a new tab). */
  openHref?: string;
  /** "Edit prompt" opens `openHref` in a new tab (the extension) instead of here (`onOpen`). */
  editInNewTab?: boolean;
  /** Asks to delete (the caller confirms). */
  onDelete?: (agent: MyBridgeAgent) => void;
  /** The viewer owns this agent (not the admin cross-bridge inspector). */
  isOwner?: boolean;
}> = ({ agent, bridgeId, canManage, expanded, onToggleExpand, onOpenConversations, fullBleed, onToggleEnabled, toggling, onOpen, openHref, editInNewTab, onDelete, isOwner }) => {
  const { t } = useTranslation(namespace);
  const purpose = agentPurpose(agent);
  // Hovering a row wakes its face up (eyes follow) or, if paused, lets it snore.
  const [rowHover, setRowHover] = useState(false);
  const open = () => onOpen?.(agent);
  /** Clicks on the controls inside the row must not open the agent. */
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  const nameCls = 'text-sm font-semibold truncate min-w-0';
  // ONE tab stop per row: the name link when there is one (Enter on it opens the
  // agent through the row click), otherwise the row itself.
  const rowIsControl = !!onOpen && !openHref;

  return (
    <div data-testid="agent-row">
      <div
        className={`flex items-center gap-3 sm:gap-4 py-3.5 ${onOpen ? `${fullBleed ? '-mx-3 sm:-mx-5 px-3 sm:px-5' : '-mx-2 px-2 rounded-ai-md'} cursor-pointer hover:bg-ai-muted transition-colors duration-(--ai-duration-fast)` : ''}`}
        onClick={onOpen ? open : undefined}
        role={rowIsControl ? 'link' : undefined}
        tabIndex={rowIsControl ? 0 : undefined}
        aria-label={rowIsControl ? agent.label : undefined}
        onKeyDown={rowIsControl ? (e) => { if (e.key === 'Enter' && e.target === e.currentTarget) open(); } : undefined}
        onMouseEnter={() => setRowHover(true)}
        onMouseLeave={() => setRowHover(false)}
        data-testid="agent-row-main"
      >
        <AgentFace agent={agent} lively={rowHover} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            {openHref ? (
              <a
                href={openHref}
                className={nameCls}
                style={{ ...text, unicodeBidi: 'plaintext' }}
                dir="auto"
                data-testid="agent-row-name"
                onClick={(e) => {
                  // A modified click keeps the link (new tab); a plain one is the row's.
                  if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) { e.stopPropagation(); return; }
                  e.preventDefault();
                }}
              >
                {agent.label}
              </a>
            ) : (
              <span className={nameCls} style={{ ...text, unicodeBidi: 'plaintext' }} dir="auto" data-testid="agent-row-name">
                {agent.label}
              </span>
            )}
            {/* FE#484 — chats a human holds: findable (and releasable) without opening Conversations. */}
            {isOwner && <span onClick={stop} onKeyDown={stop}><PausedChatsBadge agentId={agent.id} /></span>}
            {!onToggleEnabled && (
              <Badge variant={agent.enabled ? 'default' : 'secondary'} className="shrink-0" style={agent.enabled ? { backgroundColor: ACTIVE_GREEN, color: '#fff' } : undefined}>
                {agent.enabled ? t('bridgeInspector.enabled') : t('bridgeInspector.paused')}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1 text-xs mt-0.5 min-w-0" style={muted}>
            {purpose ? (
              <span className="truncate" dir="auto" data-testid="agent-row-purpose">{purpose}</span>
            ) : (
              <>
                <MessageCircle className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{t('myBridge.chatsCount', { count: agent.chatIds?.length ?? 0 })}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-none" onClick={stop} onKeyDown={stop}>
          {onToggleEnabled && (
            <Switch
              checked={agent.enabled}
              disabled={toggling}
              onCheckedChange={() => onToggleEnabled(agent)}
              aria-label={agent.enabled ? t('myBridge.pauseAria', { name: agent.label }) : t('myBridge.turnOnAria', { name: agent.label })}
              // On = the agent's own colour, the same as its face beside it.
              style={agent.enabled ? { backgroundColor: toneForAgent(agent.id) } : undefined}
              data-testid="agent-row-toggle"
            />
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label={t('myBridge.list.more')} data-testid="agent-row-menu">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {editInNewTab && openHref ? (
                <DropdownMenuItem asChild>
                  <a href={openHref} target="_blank" rel="noopener noreferrer" data-testid="agent-menu-edit">
                    <Pencil className="h-4 w-4 me-2" />
                    {t('myBridge.list.editPrompt')}
                  </a>
                </DropdownMenuItem>
              ) : onOpen && (
                <DropdownMenuItem onSelect={open} data-testid="agent-menu-edit">
                  <Pencil className="h-4 w-4 me-2" />
                  {t('myBridge.list.editPrompt')}
                </DropdownMenuItem>
              )}
              {onOpenConversations && (
                <DropdownMenuItem onSelect={() => onOpenConversations(agent)} data-testid="agent-conversations-open">
                  <MessagesSquare className="h-4 w-4 me-2" />
                  {t('bridgeInspector.conversations')}
                </DropdownMenuItem>
              )}
              {onToggleExpand && (
                <DropdownMenuItem onSelect={onToggleExpand} data-testid="agent-conversations-toggle">
                  <MessagesSquare className="h-4 w-4 me-2" />
                  {t('bridgeInspector.conversations')}
                  <ChevronDown className={`h-4 w-4 ms-auto transition-transform ${expanded ? 'rotate-180' : ''}`} />
                </DropdownMenuItem>
              )}
              {onDelete && (
                <DropdownMenuItem onSelect={() => onDelete(agent)} className="text-red-600 focus:text-red-600" data-testid="agent-delete-button">
                  <Trash2 className="h-4 w-4 me-2" />
                  {t('myBridge.delete')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {expanded && (
        <div className="pb-4">
          <AgentInspectPanel agent={agent} bridgeId={bridgeId} canManage={canManage} isOwner={isOwner} />
        </div>
      )}
    </div>
  );
};
